import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Progress } from '@app/components/common/Progress/Progress';
import { RecordingIcon } from '@app/components/common/icons/RecordingIcon';
import * as S from './Recording.styles';
import { useTranslation } from 'react-i18next';

interface RecordingProps {
  onComplete: (audioUrl: string) => void;
  onStartRecording: () => void;
  recordingTime: number;
  canStart: boolean;
}

export const Recording = forwardRef((props: RecordingProps, ref) => {
  const { t } = useTranslation();
  const [seconds, setSeconds] = useState<number>(0);
  const [permissionGranted, setPermissionGranted] = useState<boolean>(false);
  const audioChunksRef = useRef<Blob[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [micAvailable, setMicAvailable] = useState<boolean>(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioURL, setAudioURL] = useState('');

  useEffect(() => {
    const checkMicrophone = async () => {
      if (!navigator?.mediaDevices?.enumerateDevices) {
        return;
      }
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasMicrophone = devices.some((device) => device.kind === 'audioinput');
        setMicAvailable(hasMicrophone);
        if (hasMicrophone) {
          requestPermission();
        } else {
          // console.error('Microphone device missing');
        }
      } catch (error) {
        // console.error('Error checking microphone:', error);
      }
    };

    checkMicrophone();
  }, []);

  const requestPermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setPermissionGranted(true);
      // startRecording(stream);
      mediaRecorderRef.current = new MediaRecorder(stream);
    } catch (error) {
      // console.error('Lỗi khi yêu cầu quyền truy cập microphone: ', error);
      setPermissionGranted(false);
    }
  };

  const startRecording = () => {
    if (!permissionGranted || !mediaRecorderRef.current || isRecording) return;
    setAudioURL('');
    // start beep sound
    const beep = new Audio('/audio/beep.mp3');
    beep.play().catch((err) => console.error('Lỗi phát âm thanh:', err));

    setIsRecording(true);

    const mediaRecorder = mediaRecorderRef.current;
    audioChunksRef.current = [];
    mediaRecorder.ondataavailable = (event) => {
      audioChunksRef.current.push(event.data);
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(audioBlob);
      setAudioURL(audioUrl);
      props.onComplete(audioUrl);
      setIsRecording(false);
    };

    mediaRecorder.start();
    setSeconds(0);

    // Đếm ngược thời gian 40 giây
    timerRef.current = setInterval(() => {
      setSeconds((prevSeconds) => {
        if (prevSeconds >= props.recordingTime) {
          clearInterval(timerRef.current!);
          mediaRecorder.stop();
          return props.recordingTime;
        }
        return prevSeconds + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      clearInterval(timerRef.current!);
      setIsRecording(false);
    }
  };

  useImperativeHandle(ref, () => ({
    startRecording,
    stopRecording,
  }));

  if (!micAvailable) {
    return (
      <S.Box>
        <S.TextRed>{t('recording.microMissing')}</S.TextRed>
      </S.Box>
    );
  }

  return (
    <>
      <S.Box>
        {permissionGranted ? (
          <div className="flex w-full gap-3 items-center">
            <RecordingIcon
              className={props.canStart ? 'cursor-pointer' : ''}
              recording={isRecording}
              onClick={props.canStart ? props.onStartRecording : undefined}
            />
            <S.Timer>
              0:{seconds.toString().padStart(2, '0')} / 0:{props.recordingTime}
            </S.Timer>
            <Progress
              percent={(seconds / props.recordingTime) * 100}
              showInfo={false}
              strokeColor="#616161"
              trailColor="#CCCCCC"
              size="small"
              strokeLinecap="butt"
              style={{ width: 400 }}
            />
          </div>
        ) : (
          <p>{t('recording.microWaiting')}</p>
        )}
      </S.Box>
      {/* {audioURL && props.canStart && (
        <audio controls className="p-2">
          <source src={audioURL} type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      )} */}
    </>
  );
});
